/**
 * Most of the mappings that we have in this file allow for definitions of specific to generic. For
 * example you can create overrides for:
 *   - en-Latn-US - Will impact US Latin English
 *   - en-Latn - Will impact All Latin English
 *   - en - Will impact All English (Latn, Arabic, etc)
 * This function is a utility function to make it easy to get the most specific override based on
 * the locale provided
 */
export function getValueForLocale(locale, map) {
    const maximized = new Intl.Locale(locale).maximize();
    const { language, region, script } = maximized;
    const possibleKeys = [`${language}-${script}-${region}`, `${language}-${script}`];
    for (const possibleKey of possibleKeys) {
        const value = map[possibleKey];
        if (value != null) {
            return value;
        }
    }
    return null;
}
/**
 * @see valueOverrides
 */
const valueOverridesInternal = {
    "cs-Latn": {
        month: {
            short: [
                "led.",
                "ún.",
                "břz.",
                "dub.",
                "květ.",
                "čvn.",
                "čvc.",
                "srp.",
                "září",
                "říj.",
                "list.",
                // Note: If you modify the value on the next line, update the README documentation using the same value
                "pros.",
            ],
        },
    },
    "da-Latn": {
        month: {
            short: [
                undefined,
                undefined,
                "marts",
                "april",
                undefined,
                "juni",
                "juli",
                undefined,
                "sept.",
                undefined,
                undefined,
                undefined,
            ],
        },
    },
    "es-Latn": {
        month: {
            short: [
                "ene.",
                "feb.",
                "mar.",
                "abr.",
                "may.",
                "jun.",
                "jul.",
                "ago.",
                "sept.",
                "oct.",
                "nov.",
                "dic.",
            ],
        },
    },
    "hr-Latn": {
        month: {
            short: [
                "siječ.",
                "velj.",
                "ožuj.",
                "trav.",
                "svib.",
                "lip.",
                "srp.",
                "kol.",
                "ruj.",
                "list.",
                "stud.",
                "pros.",
            ],
        },
    },
    "is-Latn": {
        month: {
            long: [
                "janúar",
                "febrúar",
                "mars",
                "apríl",
                "maí",
                "júní",
                "júlí",
                "ágúst",
                "september",
                "október",
                "nóvember",
                "desember",
            ],
            narrow: ["J", "F", "M", "A", "M", "J", "J", "Á", "S", "O", "N", "D"],
            short: [
                "jan.",
                "feb.",
                "mar.",
                "apr.",
                "maí",
                "jún.",
                "júl.",
                "ágú.",
                "sep.",
                "okt.",
                "nóv.",
                "des.",
            ],
        },
        weekday: {
            long: [
                "sunnudagur",
                "mánudagur",
                "þriðjudagur",
                "miðvikudagur",
                "fimmtudagur",
                "föstudagur",
                "laugardagur",
            ],
            narrow: ["S", "M", "Þ", "M", "F", "F", "L"],
            short: ["su.", "má.", "þr.", "mi.", "fi.", "fö.", "la."],
        },
    },
    "no-Latn": {
        month: {
            short: [
                "jan.",
                "feb.",
                "mars",
                "apr.",
                undefined,
                "juni",
                "juli",
                "aug.",
                "sep.",
                "okt.",
                "nov.",
                "des.",
            ],
        },
    },
    "pl-Latn": {
        month: {
            short: [
                "stycz.",
                "luty",
                "mar.",
                "kwiec.",
                "maj",
                "czerw.",
                "lip.",
                "sierp.",
                "wrzes.",
                "paźdz.",
                "listop.",
                "grudz.",
            ],
        },
    },
    "pt-Latn": {
        month: {
            short: ["Jan", "Fev", "Mar", "Abr", "Maio", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
        },
    },
    "ro-Latn": {
        month: {
            short: [undefined, "febr.", "mart."],
        },
    },
};
/**
 * Overrides a particular FormatPart's translation depending on the options configured.
 * Anything that is undefined will use the Intl API result.
 */
export const valueOverrides = valueOverridesInternal;
/**
 * Some browsers don't fully support all locales we ship in (ex. Chrome and is-IS)
 * We have some options:
 *
 * 1. Build out the entire formatting/translation logic ourselves
 * 2. Build out the translations/number format logic ourselves but default the formatting to another language which is close enough
 *    to the missing language
 * 3. Force formatjs's polyfill to always install and then select the languages we want:
 *    https://stackoverflow.com/questions/64188266/how-to-add-missing-locale-to-intl-datetimeformat-es2020-if-browser-doesnt-sup
 *
 * We've gone with option 2 to reduce the amount of code and data stored in this package. Also we only enable it if the locale
 * returns false for Intl.DateTimeFormat.supportedLocalesOf
 */
export const unsupportedLocaleToClosetFormat = {
    "is-Latn": "de",
};
/**
 * Allows you to change any formatPart of type literal. Would be useful if you want to change text like "25th of December" to "25th in December" where "of" is the literal.
 */
export const literalOverrides = {
    // In Chrome is-IS isn't supported so we need to map the `de` translations to the `is` translations. For all other browsers it will already be " kl. " so it won't get matched.
    "is-Latn": {
        " Uhr": "",
        " Uhr ": " ",
        " um ": " kl. ",
        " ├Tageshälfte: ": " ├f.h./e.h.: ",
        AM: "f.h.",
        PM: "e.h.",
        "að kvöldi": "abends",
        "að morgni": "morgens",
        "kv.": "abends",
        "mrg.": "morgens",
    },
};
/**
 * Options to apply if the key isn't defined
 */
export const optionOverrides = {
    "ar-Arab": {
        numberingSystem: "latn",
    },
};
/**
 * @see optionValueOverrides
 */
const optionValueOverridesInternal = {};
/**
 * @see unsupportedOptionValueOverrides
 */
const unsupportedOptionValueOverridesInternal = {
    /**
     * Chrome doesn't support is-IS so instead we use de-Latn as it has the closest
     * literals to is-IS. However the values (e.g. day, month, year) still need correcting
     */
    "is-Latn": {
        dateStyle: {
            full: {
                day: "numeric",
                month: "long",
                weekday: "long",
                year: "numeric",
            },
            long: {
                day: "numeric",
                month: "long",
                year: "numeric",
            },
            medium: {
                day: "numeric",
                month: "short",
                year: "numeric",
            },
            short: {
                day: "numeric",
                month: "numeric",
                year: "numeric",
            },
        },
        timeStyle: {
            full: {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZoneName: "shortOffset",
            },
            long: {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZoneName: "shortOffset",
            },
            medium: {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            },
            short: {
                hour: "2-digit",
                minute: "2-digit",
            },
        },
    },
};
/**
 * This maps from:
 *  - Locale to match -> option key to match -> option value to match -> new option values which replace matched option value.
 *
 * Usually used to change dateStyle or timeStyle as the default might not match up with
 * what is expected by our localizers
 */
export const optionValueOverrides = optionValueOverridesInternal;
/**
 * Same as optionValueOverrides however it only applies when the locale is unsupported
 * (e.g. Intl.DateTimeFormat.supportedLocalesOf(locale).length === 0)
 */
export const unsupportedOptionValueOverrides = unsupportedOptionValueOverridesInternal;
// Useful to help unit test
valueOverrides["zz-Latn"] = valueOverrides["is-Latn"];
unsupportedLocaleToClosetFormat["zz-Latn"] = unsupportedLocaleToClosetFormat["is-Latn"];
optionValueOverrides["zz-Latn"] = optionValueOverrides["is-Latn"];
unsupportedOptionValueOverrides["zz-Latn"] = unsupportedOptionValueOverrides["is-Latn"];
literalOverrides["zz-Latn"] = literalOverrides["is-Latn"];
